import { FailedAttempt } from './types';
import { toast } from 'sonner';

const LOCKOUT_DURATION_MS = 30 * 60 * 1000; // 30 minutes
const MAX_FAILED_ATTEMPTS = 3; // 3 attempts
const FAILED_ATTEMPTS_KEY = 'failed-login-attempts';

export const getFailedAttempts = (): FailedAttempt[] => {
  const stored = localStorage.getItem(FAILED_ATTEMPTS_KEY);
  if (!stored) return [];
  
  try {
    const attempts = JSON.parse(stored) as FailedAttempt[];
    return attempts.map(attempt => ({
      ...attempt,
      lockedUntil: attempt.lockedUntil ? new Date(attempt.lockedUntil) : null
    }));
  } catch {
    return [];
  }
};

export const saveFailedAttempts = (attempts: FailedAttempt[]): void => {
  localStorage.setItem(FAILED_ATTEMPTS_KEY, JSON.stringify(attempts));
};

export const isAccountLocked = (email: string): { locked: boolean, remainingTime?: number } => {
  const attempts = getFailedAttempts();
  const userAttempt = attempts.find(a => a.email.toLowerCase() === email.toLowerCase());
  
  if (!userAttempt || !userAttempt.lockedUntil) return { locked: false };
  
  const now = new Date();
  if (now < userAttempt.lockedUntil) {
    const remainingTime = userAttempt.lockedUntil.getTime() - now.getTime();
    return { locked: true, remainingTime };
  }
  
  // If we get here, the lock time has expired
  const updatedAttempts = attempts.map(a => {
    if (a.email.toLowerCase() === email.toLowerCase()) {
      return { ...a, count: 0, lockedUntil: null };
    }
    return a;
  });
  
  saveFailedAttempts(updatedAttempts);
  return { locked: false };
};

export const recordFailedAttempt = (email: string): void => {
  const attempts = getFailedAttempts();
  const existingIndex = attempts.findIndex(a => a.email.toLowerCase() === email.toLowerCase());
  
  if (existingIndex >= 0) {
    const updatedAttempts = [...attempts];
    const currentCount = updatedAttempts[existingIndex].count + 1;
    
    updatedAttempts[existingIndex] = {
      ...updatedAttempts[existingIndex],
      count: currentCount,
      lockedUntil: currentCount >= MAX_FAILED_ATTEMPTS ? new Date(Date.now() + LOCKOUT_DURATION_MS) : null
    };
    
    saveFailedAttempts(updatedAttempts);
    
    if (currentCount >= MAX_FAILED_ATTEMPTS) {
      const minutes = LOCKOUT_DURATION_MS / (60 * 1000);
      toast.error(`Too many failed login attempts. Account locked for ${minutes} minutes.`);
    } else if (currentCount === MAX_FAILED_ATTEMPTS - 1) {
      toast.warning(`Warning: One more failed attempt will lock your account for 30 minutes.`);
    }
  } else {
    attempts.push({
      email,
      count: 1,
      lockedUntil: null
    });
    saveFailedAttempts(attempts);
  }
};

export const resetFailedAttempts = (email: string): void => {
  const attempts = getFailedAttempts();
  const updatedAttempts = attempts.filter(a => a.email.toLowerCase() !== email.toLowerCase());
  saveFailedAttempts(updatedAttempts);
};

// Keep this function for administrative purposes only
export const clearAllLockouts = (): void => {
  localStorage.removeItem(FAILED_ATTEMPTS_KEY);
};
